import * as Sentry from '@sentry/react';
import { handleErrorCode } from 'apis/forge';
import axios from 'axios';
import { EXPORT_MESSAGES } from 'dragonmouse-shared/api/export';
import { ErrorCode } from 'dragonmouse-shared/constants';
import { ExportLink } from 'dragonmouse-shared/interfaces';
import { Id, toast } from 'react-toastify';
import { selectUser } from 'redux/db/reducers';
import { showDownloadsModal } from 'redux/mode/actions';
import store from 'redux/store';
import { exportDownloadUrl, ExportUserError } from 'utils';
import * as S from './styles';

// Wait for the matching ExportLink to leave 'pending'. For legacy / non-code
// exports the entry arrives without a `status` field, which is read as 'ready'
// (terminal) immediately on the next polling tick. For code exports, this
// resolves when the BullMQ processor flips the link to 'ready' or 'failed'.
function waitForExportTerminal(exportId: string): Promise<ExportLink> {
	const check = (): ExportLink | null => {
		const link = selectUser(store.getState().db)?.metadata?.recentExports?.find(
			(e) => e.id === exportId
		);
		if (!link) return null;
		const status = link.status ?? 'ready';
		return status === 'pending' ? null : link;
	};
	const immediate = check();
	if (immediate) return Promise.resolve(immediate);
	return new Promise<ExportLink>((resolve) => {
		const unsubscribe = store.subscribe(() => {
			const link = check();
			if (link) {
				unsubscribe();
				resolve(link);
			}
		});
	});
}

interface ReadyResult {
	exportId: string;
	filename: string;
}

// Content rendered inside the react-toastify toast when an export finishes.
// The anchor needs the `download` attribute so the browser treats the click
// as a fresh user gesture (the whole reason this flow exists — Chrome
// silently blocks the second of multiple auto-triggered downloads).
const ExportReadyToast = ({ exportId, filename, toastId }: ReadyResult & { toastId: Id }) => {
	const href = exportDownloadUrl(exportId);
	return (
		<S.Base data-testid="export-ready-toast">
			<S.Message>{filename} is ready.</S.Message>
			<S.Links>
				<S.LinkAnchor
					data-testid="export-download-link"
					href={href}
					download={filename}
					onClick={(e) => {
						e.stopPropagation();
						toast.dismiss(toastId);
					}}
				>
					Download
				</S.LinkAnchor>
				<S.LinkButton
					type="button"
					onClick={(e) => {
						e.stopPropagation();
						store.dispatch(showDownloadsModal());
						toast.dismiss(toastId);
					}}
				>
					Open Downloads
				</S.LinkButton>
			</S.Links>
		</S.Base>
	);
};

interface RunExportArgs<T extends ReadyResult> {
	// Ack toast copy shown immediately on submit; auto-dismissed the moment
	// the ready (or failed) toast fires, so the two never coexist. Defaults
	// to a generic 'Export started…' message — safe for any future export.
	pendingLabel?: string;
	run: () => Promise<T>;
}

// The toast for an export request that failed before it got going. Structured errors
// are handled by their own UI (e.g. the out-of-credits modal) and get no toast; an
// export error written for the user is shown as-is; anything else gets the generic copy.
function showRequestFailure(e: unknown) {
	if (handleErrorCode(e)) return;
	const data = axios.isAxiosError(e) ? e.response?.data : undefined;
	const message =
		e instanceof ExportUserError
			? e.message
			: data?.errorCode === ErrorCode.EXPORT_FAILED && typeof data.error === 'string'
			? data.error
			: EXPORT_MESSAGES.failed;
	toast.error(message, { autoClose: 5000, closeOnClick: true });
}

// Background-task UX: submit ack auto-dismisses, completion fires its own
// toast. Persistent indicators on the Export button and View Downloads carry
// the in-flight state in the meantime (see DownloadsMenuItem / TopRight).
//
// The ack fires *before* `run()` because some export endpoints (frames-ai,
// ds-ai) are synchronous on the wire — the HTTP request stays open for the
// full LLM duration. Firing the ack after `run()` would mean the user sees
// no feedback for seconds, then the ack and ready toasts pop simultaneously.
//
// `run()` resolves either after the export completed (sync handlers) or
// after the job was enqueued (code-export). Either way we wait on Redux for
// the corresponding ExportLink to leave 'pending', so the completion toast
// reflects the *final* outcome — and a tab close anywhere between submit
// and completion doesn't lose anything because the next ProfilePollingStream
// tick will deliver the terminal state.
export async function runExportWithToast<T extends ReadyResult>({
	pendingLabel,
	run,
}: RunExportArgs<T>): Promise<T> {
	const ackToastId = toast.success(
		pendingLabel ?? 'Export started — we\'ll notify you when it\'s ready.',
		{ autoClose: 4000, closeOnClick: true }
	);

	let result: T;
	try {
		result = await run();
	} catch (e) {
		Sentry.captureException(e);
		toast.dismiss(ackToastId);
		showRequestFailure(e);
		throw e;
	}

	// Fire-and-forget: completion may be milliseconds (sync handlers) or many
	// minutes (code-export job). Either way the caller doesn't wait for it.
	void waitForExportTerminal(result.exportId).then((link) => {
		toast.dismiss(ackToastId);
		const status = link.status ?? 'ready';
		if (status === 'ready') {
			toast.success(
				({ toastProps }) => (
					<ExportReadyToast
						exportId={result.exportId}
						filename={result.filename}
						toastId={toastProps.toastId}
					/>
				),
				{ autoClose: false, closeOnClick: false, closeButton: true }
			);
		} else {
			toast.error(link.error || EXPORT_MESSAGES.failed, { autoClose: 5000, closeOnClick: true });
		}
	});

	return result;
}

export default ExportReadyToast;
